import React, { useCallback } from 'react';
import { Dropdown } from '@dexma/ui-components';
import Dashboard from 'domain/entity/dashboard';
import { getActionsDropdownContent } from 'adapter/in/dashboards/components/DashboardActionsDropdown/DashboardActionsDropdownUtils';
import { useOnDeleteDashboardModal } from 'adapter/in/dashboards/containers/Modals/DeleteDashboardModal/useOnDeleteDashboardModal';
import { useOnLinkDashboardModal } from 'adapter/in/dashboards/containers/Modals/LinkDashboardModal/useOnLinkDashboardModal';
import { useOnShareDashboardModal } from 'adapter/in/dashboards/containers/Modals/ShareDashboardModal/useOnShareDashboardModal';
import { useOnPinDashboardModal } from 'adapter/in/dashboards/containers/Modals/PinDashboardModal/useOnPinDashboardModal';
import { useOnDuplicateDashboardModal } from 'adapter/in/dashboards/containers/Modals/DuplicateDashboardModal/useOnDuplicateDashboardModal';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import gtm from 'adapter/in/dashboards/config/gtm';
import i18n from 'asset/translation';

export type DashboardActionsDropdownProps = {
    dashboard: Dashboard;
    onDeleteModalSuccess: () => void;
    onShareModalSuccess: () => void;
    onPinModalSuccess: (pinned: boolean) => void;
    onDuplicateModalSuccess: () => void;
    hasPinnedDashboard: boolean;
    withDuplicateRedirection: boolean;
};
export const DashboardActionsDropdown = (props: DashboardActionsDropdownProps) => {
    const {
        dashboard,
        onDeleteModalSuccess,
        onShareModalSuccess,
        onPinModalSuccess,
        onDuplicateModalSuccess,
        hasPinnedDashboard,
        withDuplicateRedirection,
    } = props;
    const { navigationPush } = useNavigation();
    const handleOnEdit = useCallback((dashboardId: string) => navigationPush(`${dashboardId}/edit`), []);
    const { handleOnDelete, onDeleteModal } = useOnDeleteDashboardModal(onDeleteModalSuccess);
    const { handleOnLink, onLinkModal } = useOnLinkDashboardModal();
    const { handleOnShare, onShareModal } = useOnShareDashboardModal(onShareModalSuccess);
    const { handleOnPin, onPinModal } = useOnPinDashboardModal(hasPinnedDashboard, onPinModalSuccess);
    const { handleOnDuplicate, onDuplicateModal } = useOnDuplicateDashboardModal(onDuplicateModalSuccess, withDuplicateRedirection);
    const content = getActionsDropdownContent(
        dashboard.permittedActions.edit ? () => handleOnEdit(dashboard.id) : undefined,
        dashboard.permittedActions.duplicate ? () => handleOnDuplicate(dashboard) : undefined,
        dashboard.permittedActions.link ? () => handleOnLink(dashboard) : undefined,
        dashboard.permittedActions.share ? () => handleOnShare(dashboard) : undefined,
        dashboard.permittedActions.pin && !dashboard.pinned ? () => handleOnPin(dashboard) : undefined,
        dashboard.permittedActions.pin && dashboard.pinned ? () => handleOnPin(dashboard) : undefined,
        dashboard.permittedActions.delete ? () => handleOnDelete(dashboard.id) : undefined
    );

    const onItemDropdownSelected = (key: number) => {
        const itemSelected = content[key];
        switch (itemSelected.id) {
            case 0:
                handleOnEdit(dashboard.id);
                break;
            case 1:
                handleOnDuplicate(dashboard);
                break;
            case 2:
                handleOnLink(dashboard);
                break;
            case 3:
                handleOnShare(dashboard);
                break;
            case 4:
            case 5:
                handleOnPin(dashboard);
                break;
            case 6:
                handleOnDelete(dashboard.id);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Dropdown
                trigger={['click']}
                icon='more_horiz'
                dataId={gtm.dashboard.actions.title}
                content={content}
                iconAriaLabel={i18n.t('dashboards.ariaLabel.moreActions')}
                onItemSelected={onItemDropdownSelected}
            />
            {onDuplicateModal}
            {onDeleteModal}
            {onPinModal}
            {onLinkModal}
            {onShareModal}
        </>
    );
};
